import { FC, useState, MouseEvent as ReactMouseEvent } from 'react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { PlusIcon } from '@phosphor-icons/react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { ICoachSessionListItem } from '../../endpoints/coachSessions';
import { OBJECTIVE_COLORS, OBJECTIVE_LABELS } from './useCoachSessionWeekView';
import { SessionContextMenu } from './SessionContextMenu';
import styles from './CoachSessionWeekView.module.scss';

interface WeekStripProps {
  weekDays: dayjs.Dayjs[];
  sessionsByDate: Record<string, ICoachSessionListItem[]>;
  teamId: string;
  today: dayjs.Dayjs;
  onCreateSession: (date: string) => void;
  onDuplicate: (session: ICoachSessionListItem) => void;
  onMove: (session: ICoachSessionListItem) => void;
  onDelete: (session: ICoachSessionListItem) => void;
  onExportPdf: (session: ICoachSessionListItem) => void;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Bozza',
  published: 'Pubblicata',
  executed: 'Eseguita',
};

const DAY_NAMES = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

interface SessionCardProps {
  session: ICoachSessionListItem;
  teamId: string;
  onContextMenu: (ev: ReactMouseEvent, session: ICoachSessionListItem) => void;
}

const SessionCard: FC<SessionCardProps> = ({
  session,
  teamId,
  onContextMenu,
}) => {
  const navigate = useNavigate();
  const obj = session.objective ?? 'mixed';
  const color = OBJECTIVE_COLORS[obj] ?? '#b5b5b5';
  const label = OBJECTIVE_LABELS[obj] ?? obj;
  const m = session.metrics;

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: session._id,
      disabled: session.status === 'executed',
    });

  // a48bb7: il click apre la VISTA di sola lettura; da lì "Modifica" porta all'editor.
  const openDetail = () =>
    navigate(`/dashboard/teams/${teamId}/sessions/${session._id}/view`);

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.35 : 1,
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    cursor: session.status === 'executed' ? 'pointer' : 'grab',
  };

  return (
    <div
      ref={setNodeRef}
      className={styles.sessionCard}
      style={style}
      onClick={openDetail}
      onContextMenu={(e) => onContextMenu(e, session)}
      {...listeners}
      {...attributes}
    >
      {session.startTime && (
        <div className={styles.cardTime}>{session.startTime}</div>
      )}
      <div className={styles.cardTitle}>{session.title}</div>
      <div className={styles.cardMeta}>
        <span className={`${styles.statusPill} ${styles[session.status]}`}>
          {STATUS_LABELS[session.status] ?? session.status}
        </span>
        <span
          className={styles.objectivePill}
          style={{ background: `${color}20`, color }}
        >
          {label}
        </span>
      </div>
      {m && (
        <div className={styles.cardMetrics}>
          {m.totalDurationMinutes != null && (
            <span className={styles.metricChip}>
              {m.totalDurationMinutes}'
            </span>
          )}
          {m.ewtMinutes != null && (
            <span className={styles.metricChip}>EWT {m.ewtMinutes}'</span>
          )}
          {m.avgIPS != null && (
            <span className={styles.metricChip}>
              IPS {m.avgIPS.toFixed(1)}
            </span>
          )}
          {m.avgCLS != null && (
            <span className={styles.metricChip}>
              CLS {m.avgCLS.toFixed(1)}
            </span>
          )}
          {m.totalCE != null && (
            <span className={styles.metricChip}>
              CE {m.totalCE.toFixed(1)}
            </span>
          )}
        </div>
      )}
      <div className={styles.cardFooter}>
        <span className={styles.cardFooterInfo}>
          {session.blocksCount ?? 0} blocchi
        </span>
        {session.athletesCount != null && (
          <span className={styles.cardFooterInfo}>
            {session.athletesCount} atleti
          </span>
        )}
      </div>
    </div>
  );
};

interface DayColumnProps {
  day: dayjs.Dayjs;
  dayIndex: number;
  isToday: boolean;
  daySessions: ICoachSessionListItem[];
  teamId: string;
  onCreateSession: (date: string) => void;
  onContextMenu: (ev: ReactMouseEvent, session: ICoachSessionListItem) => void;
}

const DayColumn: FC<DayColumnProps> = ({
  day,
  dayIndex,
  isToday,
  daySessions,
  teamId,
  onCreateSession,
  onContextMenu,
}) => {
  const key = day.format('YYYY-MM-DD');
  const { setNodeRef, isOver } = useDroppable({ id: key });

  return (
    <div
      ref={setNodeRef}
      className={`${styles.dayColumn} ${isOver ? styles.dayColumnOver : ''}`}
    >
      <div
        className={`${styles.dayHeader} ${
          isToday ? styles.dayHeaderToday : ''
        }`}
      >
        <div className={styles.dayName}>{DAY_NAMES[dayIndex]}</div>
        <div className={styles.dayNumber}>{day.format('D')}</div>
      </div>

      {daySessions.map((s) => (
        <SessionCard
          key={s._id}
          session={s}
          teamId={teamId}
          onContextMenu={onContextMenu}
        />
      ))}

      <div
        className={styles.emptySlot}
        onClick={() => onCreateSession(key)}
        title="Pianifica sessione"
      >
        <PlusIcon size={14} color="#b0b0b0" />
        <span className={styles.emptySlotLabel}>Pianifica</span>
      </div>
    </div>
  );
};

export const WeekStrip: FC<WeekStripProps> = ({
  weekDays,
  sessionsByDate,
  teamId,
  today,
  onCreateSession,
  onDuplicate,
  onMove,
  onDelete,
  onExportPdf,
}) => {
  const [menu, setMenu] = useState<{
    x: number;
    y: number;
    session: ICoachSessionListItem;
  } | null>(null);

  const onContextMenu = (
    ev: ReactMouseEvent,
    session: ICoachSessionListItem,
  ) => {
    ev.preventDefault();
    ev.stopPropagation();
    setMenu({ x: ev.clientX, y: ev.clientY, session });
  };

  return (
    <>
      <div className={styles.weekGrid}>
        {weekDays.map((day, idx) => {
          const key = day.format('YYYY-MM-DD');
          const isToday = day.isSame(today, 'day');
          const daySessions = sessionsByDate[key] ?? [];
          return (
            <DayColumn
              key={key}
              day={day}
              dayIndex={idx}
              isToday={isToday}
              daySessions={daySessions}
              teamId={teamId}
              onCreateSession={onCreateSession}
              onContextMenu={onContextMenu}
            />
          );
        })}
      </div>

      {menu && (
        <SessionContextMenu
          x={menu.x}
          y={menu.y}
          onDuplicate={() => onDuplicate(menu.session)}
          onMove={() => onMove(menu.session)}
          onDelete={() => onDelete(menu.session)}
          onExportPdf={() => onExportPdf(menu.session)}
          onClose={() => setMenu(null)}
        />
      )}
    </>
  );
};
